import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import * as featureService from 'features/fflip-helpers';
import { RefusalDialogWrapper } from 'containers/CourtManagerLegalProceedings/JudgementAdd/styled-components';
import { FEATURES } from 'features/features';
import InfoIcon from 'images/info-ic.svg';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { Typography, Grid, TextField } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import {
  DATE_FORMAT,
  DATE_PICKET_FORMAT,
  DOCUMENT_TYPES,
} from 'constants/global';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { EMPTY_DOCUMENT } from 'containers/data-structure';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { get, isNil } from 'lodash';
import { compareDate } from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';

export interface UpdateFirmReferenceDialogProps {
  intl: IntlShape;
  isDialogOpen: boolean;
  setIsDialogOpen: Function;
  requestUpdateFirmReference: Function;
  claimId: number;
  firmReference: string;
  lawyerClaimAssignmentId: number;
  isFeeNoteGenerated?: boolean;
  requestCreateFeeNote?: Function;
  feeNote?: any;
  judiciaryProcessId?: number;
  judgementId?: number;
  judgementAddDocumentIds?: any[];
  lawyerFirmReference?: boolean;
  resetUploadedDocument: Function;
  uploadedDocument: any;
  uploadDocument: Function;
  listings: any;
  limitInvoiceDate: any;
}

export const UpdateFirmReferenceDialog: React.FC<UpdateFirmReferenceDialogProps> = (
  props,
): JSX.Element => {
  const [invoiceDocument, setInvoiceDocument] = React.useState({});
  const [invoiceNumber, setInvoiceNumber] = React.useState(null);
  const [invoiceDate, setInvoiceDate] = React.useState(null);

  const handleChangeInvoiceNumber = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    setInvoiceNumber(event.target.value as any);
  };

  const handleChangeInvoiceDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const invoiceDate = moment(theDate as Date).format(DATE_FORMAT);
    setInvoiceDate(invoiceDate as any);
  };

  const handleChangeDocument = (document: any) => {
    setInvoiceDocument(document);
  };
  const handleSuccessCallback = () => {
    props.isFeeNoteGenerated
      ? toast.success(
          translate(messages.lawyerFirmReferenceDialogSuccessMessage, props),
        )
      : toast.success(
          translate(
            messages.lawyerFirmReferenceDialogSuccessFeeNoteMessage,
            props,
          ),
        );
    props.setIsDialogOpen(false);
  };
  const handleValidation = () => {
    const validationRequest = {
      id: props.lawyerClaimAssignmentId,
      firmReference: props.firmReference,
      judiciaryProcessId: props.judiciaryProcessId,
      claimId: props.claimId,
      isTrackingActive: featureService.hasFeature(
        FEATURES.TRACKING_ACTIONS_USER,
      ),
      username:
        currentIntervenantStorage?.get() &&
        currentIntervenantStorage?.get()?.preferred_username,
      isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
    };
    if (props.isFeeNoteGenerated) {
      props.requestUpdateFirmReference(
        {
          ...validationRequest,
        },
        handleSuccessCallback,
      );
    } else {
      props.requestCreateFeeNote &&
        props.requestCreateFeeNote(
          {
            ...props.feeNote,
            invoiceFeeNote: featureService.hasFeature(
              FEATURES.BILLING_INFO_FEE_NOTE,
            )
              ? {
                  codeIntervenant: currentIntervenantStorage.get()?.sagis_code,
                  createdBy:
                    currentIntervenantStorage?.get() &&
                    currentIntervenantStorage?.get()?.preferred_username,
                  invoiceNumber: invoiceNumber,
                  invoiceDate:
                    invoiceDate === 'Invalid date' ? null : invoiceDate,
                  receptionDate: moment().format('YYYY-MM-DD'),
                  ...invoiceDocument,
                  isIndexed: false,
                }
              : undefined,
            judiciaryProcessId: props.judiciaryProcessId,
            claimId: props.claimId,
            isInboxesActive: featureService.hasFeature(
              FEATURES.ATTRIBUTION_INBOXES_MANAGER,
            ),
            isTrackingActive: featureService.hasFeature(
              FEATURES.TRACKING_ACTIONS_USER,
            ),
          },
          handleSuccessCallback,
        );
    }
  };

  return (
    <RefusalDialogWrapper
      className="refusal-content"
      handleValidate={handleValidation}
      handleClose={() => props.setIsDialogOpen(false)}
      title={
        props.isFeeNoteGenerated
          ? translate(messages.lawyerFirmReferenceDialogTitle, props)
          : translate(messages.lawyerFirmReferenceDialogFeeNoteTitle, props)
      }
      cancelTitle={translate(messages.lawyerFirmReferenceDialogCancel, props)}
      confirmTitle={translate(
        messages.lawyerFirmReferenceDialogValidate,
        props,
      )}
      open={props.isDialogOpen}
      validateButtonDisabled={
        !props.isFeeNoteGenerated &&
        featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE)
          ? isNil(invoiceNumber) ||
            isNil(invoiceDate) ||
            invoiceDate === 'Invalid date' ||
            isNil(get(invoiceDocument, 'url')) ||
            get(invoiceDocument, 'url') === '' ||
            invoiceNumber === '' ||
            (featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              compareDate(moment().format('YYYY-MM-DD'), invoiceDate) >=
                props.limitInvoiceDate)
          : false
      }
    >
      <div className="reason-wrapper">
        <span>
          {props.isFeeNoteGenerated
            ? translate(messages.lawyerFirmReferenceWarningMessage, props)
            : translate(
                messages.lawyerFirmReferenceFeeNoteWarningMessage,
                props,
                {
                  firmReference: props.firmReference,
                },
              )}
        </span>
        {props.isFeeNoteGenerated ? (
          <></>
        ) : (
          featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) && (
            <div style={{ marginTop: 10 }}>
              <Grid
                container
                direction="row"
                alignItems="center"
                alignContent="center"
                spacing={1}
              >
                <Grid item>
                  <img src={InfoIcon} alt="infoIcon" />
                </Grid>
                <Grid item>
                  <Typography className="success-title">
                    {translate(messages.dialogInfoSubTitle, props)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={3}
                style={{ marginTop: 10 }}
              >
                <Grid item xs={6}>
                  <TextField
                    label={translate(messages.invoiceNumberField, props)}
                    name={'INVOICE_NUMBER'}
                    value={invoiceNumber}
                    fullWidth
                    onChange={handleChangeInvoiceNumber}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MuiPickersUtilsProvider
                    utils={DateFnsUtils}
                    locale={frLocale}
                  >
                    <KeyboardDatePicker
                      autoOk
                      variant="inline"
                      format={DATE_PICKET_FORMAT}
                      id="invoice-date"
                      fullWidth
                      onChange={handleChangeInvoiceDate}
                      value={dateStringToLocalDate(invoiceDate)}
                      label={translate(messages.invoiceDateField, props)}
                      error={
                        featureService.hasFeature(
                          FEATURES.LIMIT_INVOICE_DATE,
                        ) &&
                        compareDate(
                          moment().format('YYYY-MM-DD'),
                          invoiceDate,
                        ) >= props.limitInvoiceDate
                      }
                      helperText={
                        featureService.hasFeature(
                          FEATURES.LIMIT_INVOICE_DATE,
                        ) &&
                        !isNil(invoiceDate) &&
                        compareDate(
                          moment().format('YYYY-MM-DD'),
                          invoiceDate,
                        ) >= props.limitInvoiceDate &&
                        translate(messages.errorInvoiceDateIntervenant, props, {
                          limitDaysIntervenant: props.limitInvoiceDate,
                        })
                      }
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
              </Grid>
              <Grid xs={6} style={{ marginTop: 20 }}>
                <UploadDocumentBloc
                  index={0}
                  document={invoiceDocument as any}
                  setDocument={handleChangeDocument}
                  resetUploadedDocument={props.resetUploadedDocument}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  docType={DOCUMENT_TYPES.INVOICE_DOCUMENT}
                  deleteDoc={() =>
                    setInvoiceDocument({
                      ...EMPTY_DOCUMENT,
                    })
                  }
                />
              </Grid>
            </div>
          )
        )}
      </div>
    </RefusalDialogWrapper>
  );
};

export default injectIntl(UpdateFirmReferenceDialog);
